import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { GraduationCap, Lock, CheckCircle2, ArrowRight, AlertCircle, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { ArabicLevelBlock } from "../components/ArabicLevelFields";

const MATIERES = [
  { key: "arabe", label: "Arabe" },
  { key: "coran", label: "Coran" },
  { key: "education_musulmane", label: "Éducation Musulmane" },
];

function computeAge(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
}

export default function ParentReenrollmentPage() {
  const { user } = useAuth();
  const { reenrollmentOpen, loading: settingsLoading } = useSettings();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openChild, setOpenChild] = useState(null); // { source_request, ... }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/parent/reenrollment-source");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (settingsLoading || loading) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-[#5c6374]" data-testid="reenrollment-loading">Chargement…</div>;
  }

  if (!reenrollmentOpen) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center fade-in" data-testid="reenrollment-closed-page">
        <div className="w-16 h-16 rounded-full bg-[#E2E5EE] flex items-center justify-center mx-auto">
          <Lock className="w-7 h-7 text-[#5c6374]" strokeWidth={1.5} />
        </div>
        <h1 className="mt-5 font-[Outfit] font-semibold text-3xl text-[#1A2547]">Réinscriptions fermées</h1>
        <p className="mt-3 text-[#5C6861] leading-relaxed">
          Les demandes de réinscription ne sont pas ouvertes pour le moment. Merci de revenir ultérieurement.
        </p>
        <Link to="/parent" className="inline-block mt-8">
          <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]">Retour à mon espace</Button>
        </Link>
      </div>
    );
  }

  const items = data?.items || [];

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 fade-in" data-testid="reenrollment-page">
      <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C86444]">Espace Parent</span>
      <h1 className="mt-3 font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight text-[#1A2547]">
        Réinscription pour la nouvelle année
      </h1>
      <p className="text-[#5C6861] mt-3 leading-relaxed">
        Bonjour {user?.name}. Voici les enfants déjà inscrits les années précédentes. Cliquez sur <strong>Réinscrire</strong> pour pré-remplir le formulaire et confirmer leur inscription pour la nouvelle année scolaire.
      </p>

      {items.length === 0 && (
        <div className="mt-10 bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5c6374]" data-testid="no-children">
          <ClipboardCheck className="w-12 h-12 mx-auto text-[#1E3A8A]/50" strokeWidth={1.5} />
          <p className="mt-4">Aucun enfant éligible à la réinscription. Si vous souhaitez inscrire un nouvel enfant, utilisez la <Link to="/" className="underline text-[#1E3A8A]">nouvelle demande d'inscription</Link>.</p>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => (
          <ChildCard key={it.source_request.id} item={it} onReenroll={() => setOpenChild(it)} />
        ))}
      </div>

      <Dialog open={!!openChild} onOpenChange={(o) => !o && setOpenChild(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Réinscription — {openChild?.source_request?.enf_prenom} {openChild?.source_request?.enf_nom}</DialogTitle>
            <DialogDescription>
              Vérifiez les informations ci-dessous et corrigez si nécessaire avant de valider la réinscription.
            </DialogDescription>
          </DialogHeader>
          {openChild && (
            <ReenrollmentForm
              source={openChild.source_request}
              onSuccess={() => { setOpenChild(null); load(); }}
              onCancel={() => setOpenChild(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChildCard({ item, onReenroll }) {
  const r = item.source_request;
  const already = item.already_reenrolled;
  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-6" data-testid={`child-card-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#E0E7FF] flex items-center justify-center text-[#1E3A8A] shrink-0">
          <GraduationCap className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547]">{r.enf_prenom} {r.enf_nom}</h3>
          <p className="text-xs text-[#5c6374] mt-1">
            Né(e) le {r.enf_date_naissance ? new Date(r.enf_date_naissance).toLocaleDateString("fr-FR") : "—"}
            {r.enf_age && <> · {r.enf_age} ans</>}
          </p>
          <div className="text-xs text-[#8a92a3] mt-2">Année précédente : matières {(r.assigned_matieres || []).join(", ") || "—"}</div>
        </div>
      </div>
      <div className="mt-4">
        {already ? (
          <Badge className="bg-[#E0F2E9] text-[#1E7A4D] border-0 rounded-full" data-testid={`child-already-${r.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Déjà réinscrit{item.active_year_request_status === "pending" ? " (en attente)" : ""}
          </Badge>
        ) : (
          <Button onClick={onReenroll} className="rounded-full bg-[#C86444] hover:bg-[#A8533A] text-white w-full" data-testid={`reenroll-btn-${r.id}`}>
            Réinscrire <ArrowRight className="ml-1.5 w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function ReenrollmentForm({ source, onSuccess, onCancel }) {
  // Pré-remplir tous les champs depuis la source
  const initial = {
    rl_prenom: source.rl_prenom || "",
    rl_nom: source.rl_nom || "",
    rl_tel_fixe: source.rl_tel_fixe || "",
    rl_tel_portable: source.rl_tel_portable || "",
    rl_email: source.rl_email || "",
    rl_participe_activites_mosquee: !!source.rl_participe_activites_mosquee,
    rl_activites_mosquee: source.rl_activites_mosquee || "",
    rl_prieres_regulieres_mosquee: !!source.rl_prieres_regulieres_mosquee,
    enf_prenom: source.enf_prenom || "",
    enf_nom: source.enf_nom || "",
    enf_adresse: source.enf_adresse || "",
    enf_ville: source.enf_ville || "",
    enf_date_naissance: source.enf_date_naissance || "",
    enf_age: source.enf_age ?? computeAge(source.enf_date_naissance),
    enf_autorisation_rentrer_seul: !!source.enf_autorisation_rentrer_seul,
    a_des_freres_soeurs: !!source.a_des_freres_soeurs,
    freres_soeurs_details: source.freres_soeurs_details || "",
    premiere_inscription: false,
    cours_arabe_anterieur: source.cours_arabe_anterieur || "",
    groupe_souhaite: source.groupe_souhaite || "",
    photo_path: source.photo_path || "",
    niveau_arabe_ecrit: source.niveau_arabe_ecrit ?? 0,
    niveau_arabe_oral: source.niveau_arabe_oral ?? 0,
    nb_sourates: source.nb_sourates ?? 0,
    sourates_connues: Array.isArray(source.sourates_connues) ? source.sourates_connues : [],
    traitement_medical: !!source.traitement_medical,
    traitement_medical_detail: source.traitement_medical_detail || "",
    allergies: !!source.allergies,
    allergies_detail: source.allergies_detail || "",
    allergies_alimentaires: !!source.allergies_alimentaires,
    allergies_alimentaires_detail: source.allergies_alimentaires_detail || "",
    maladies_chroniques: !!source.maladies_chroniques,
    maladies_chroniques_detail: source.maladies_chroniques_detail || "",
    autres_renseignements: source.autres_renseignements || "",
    urg_prenom: source.urg_prenom || "",
    urg_nom: source.urg_nom || "",
    urg_tel_fixe: source.urg_tel_fixe || "",
    urg_tel_portable: source.urg_tel_portable || "",
    autorisation_photos_classe: !!source.autorisation_photos_classe,
    matieres_suivies: source.matieres_suivies?.length ? source.matieres_suivies : ["arabe", "coran", "education_musulmane"],
  };

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMat = (k) => set("matieres_suivies", form.matieres_suivies.includes(k)
    ? form.matieres_suivies.filter((x) => x !== k)
    : [...form.matieres_suivies, k]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.matieres_suivies.length === 0) {
      toast.error("Sélectionnez au moins une matière souhaitée");
      return;
    }
    setSubmitting(true);
    try {
      const dataPayload = {
        ...form,
        enf_age: form.enf_age ? parseInt(form.enf_age) : computeAge(form.enf_date_naissance) || null,
        nombre_matieres: form.matieres_suivies.length,
        rl_tel_fixe: form.rl_tel_fixe || null,
        urg_tel_fixe: form.urg_tel_fixe || null,
        photo_path: form.photo_path || null,
        cours_arabe_anterieur: form.cours_arabe_anterieur || null,
        groupe_souhaite: form.groupe_souhaite || null,
        freres_soeurs_details: form.freres_soeurs_details || null,
        traitement_medical_detail: form.traitement_medical_detail || null,
        allergies_detail: form.allergies_detail || null,
        allergies_alimentaires_detail: form.allergies_alimentaires_detail || null,
        maladies_chroniques_detail: form.maladies_chroniques_detail || null,
        autres_renseignements: form.autres_renseignements || null,
        rl_activites_mosquee: form.rl_activites_mosquee || null,
      };
      await api.post("/parent/reenrollment", {
        source_request_id: source.id,
        data: dataPayload,
      });
      toast.success("Réinscription envoyée. Notre équipe vous recontactera.");
      onSuccess();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6 mt-2" data-testid="reenrollment-form">
      <div className="bg-[#FFF5E5] border border-[#C86444]/30 rounded-xl p-3 flex items-start gap-2 text-xs text-[#1A2547]">
        <AlertCircle className="w-4 h-4 text-[#C86444] shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>Les informations ci-dessous proviennent de l'inscription précédente. Vérifiez et mettez à jour si nécessaire.</div>
      </div>

      <Section title="Représentant légal">
        <Grid2>
          <Field label="Prénom *"><Input required value={form.rl_prenom} onChange={(e) => set("rl_prenom", e.target.value)} className="rounded-xl" data-testid="re-rl_prenom" /></Field>
          <Field label="Nom *"><Input required value={form.rl_nom} onChange={(e) => set("rl_nom", e.target.value)} className="rounded-xl" data-testid="re-rl_nom" /></Field>
          <Field label="Téléphone portable *"><Input required value={form.rl_tel_portable} onChange={(e) => set("rl_tel_portable", e.target.value)} className="rounded-xl" data-testid="re-rl_tel_portable" /></Field>
          <Field label="Téléphone fixe"><Input value={form.rl_tel_fixe} onChange={(e) => set("rl_tel_fixe", e.target.value)} className="rounded-xl" /></Field>
        </Grid2>
        <Field label="Email *"><Input type="email" required value={form.rl_email} onChange={(e) => set("rl_email", e.target.value)} className="rounded-xl" data-testid="re-rl_email" /></Field>
      </Section>

      <Section title="Enfant">
        <Grid2>
          <Field label="Prénom *"><Input required value={form.enf_prenom} onChange={(e) => set("enf_prenom", e.target.value)} className="rounded-xl" data-testid="re-enf_prenom" /></Field>
          <Field label="Nom *"><Input required value={form.enf_nom} onChange={(e) => set("enf_nom", e.target.value)} className="rounded-xl" data-testid="re-enf_nom" /></Field>
          <Field label="Date de naissance *"><Input type="date" required value={form.enf_date_naissance} onChange={(e) => { set("enf_date_naissance", e.target.value); set("enf_age", computeAge(e.target.value)); }} className="rounded-xl" data-testid="re-enf_date_naissance" /></Field>
          <Field label="Âge"><Input type="number" value={form.enf_age} onChange={(e) => set("enf_age", e.target.value)} className="rounded-xl" /></Field>
        </Grid2>
        <Field label="Adresse *"><Input required value={form.enf_adresse} onChange={(e) => set("enf_adresse", e.target.value)} className="rounded-xl" data-testid="re-enf_adresse" /></Field>
        <Field label="Ville *"><Input required value={form.enf_ville} onChange={(e) => set("enf_ville", e.target.value)} className="rounded-xl" data-testid="re-enf_ville" /></Field>
      </Section>

      <Section title="Matières souhaitées">
        <div className="flex flex-wrap gap-3">
          {MATIERES.map((m) => (
            <label key={m.key} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F4F1EB] border border-[#EAE6DF] cursor-pointer">
              <Checkbox checked={form.matieres_suivies.includes(m.key)} onCheckedChange={() => toggleMat(m.key)} data-testid={`re-mat-${m.key}`} />
              <span className="text-sm">{m.label}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Niveau d'arabe & Coran (mise à jour)">
        <p className="text-xs text-[#5c6374] -mt-2">Ajustez le niveau de votre enfant pour la nouvelle année.</p>
        <ArabicLevelBlock form={form} set={set} testIdPrefix="re" />
      </Section>

      <Section title="Médical / allergies (mise à jour si besoin)">
        <CheckRow label="Traitement médical" checked={form.traitement_medical} onChange={(v) => set("traitement_medical", v)} detail={form.traitement_medical_detail} onDetail={(v) => set("traitement_medical_detail", v)} />
        <CheckRow label="Allergies" checked={form.allergies} onChange={(v) => set("allergies", v)} detail={form.allergies_detail} onDetail={(v) => set("allergies_detail", v)} />
        <CheckRow label="Allergies alimentaires" checked={form.allergies_alimentaires} onChange={(v) => set("allergies_alimentaires", v)} detail={form.allergies_alimentaires_detail} onDetail={(v) => set("allergies_alimentaires_detail", v)} />
        <CheckRow label="Maladies chroniques" checked={form.maladies_chroniques} onChange={(v) => set("maladies_chroniques", v)} detail={form.maladies_chroniques_detail} onDetail={(v) => set("maladies_chroniques_detail", v)} />
      </Section>

      <Section title="Contact en cas d'urgence">
        <Grid2>
          <Field label="Prénom *"><Input required value={form.urg_prenom} onChange={(e) => set("urg_prenom", e.target.value)} className="rounded-xl" data-testid="re-urg_prenom" /></Field>
          <Field label="Nom *"><Input required value={form.urg_nom} onChange={(e) => set("urg_nom", e.target.value)} className="rounded-xl" data-testid="re-urg_nom" /></Field>
          <Field label="Téléphone portable *"><Input required value={form.urg_tel_portable} onChange={(e) => set("urg_tel_portable", e.target.value)} className="rounded-xl" data-testid="re-urg_tel_portable" /></Field>
          <Field label="Téléphone fixe"><Input value={form.urg_tel_fixe} onChange={(e) => set("urg_tel_fixe", e.target.value)} className="rounded-xl" /></Field>
        </Grid2>
      </Section>

      <Section title="Autorisations">
        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox checked={form.autorisation_photos_classe} onCheckedChange={(v) => set("autorisation_photos_classe", !!v)} className="mt-0.5" data-testid="re-autorisation_photos_classe" />
          <span className="text-sm text-[#5c6374]">J'autorise la prise de photos de classe</span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox checked={form.enf_autorisation_rentrer_seul} onCheckedChange={(v) => set("enf_autorisation_rentrer_seul", !!v)} className="mt-0.5" />
          <span className="text-sm text-[#5c6374]">J'autorise mon enfant à rentrer seul après les cours</span>
        </label>
      </Section>

      <Field label="Autres renseignements (facultatif)">
        <Textarea value={form.autres_renseignements} onChange={(e) => set("autres_renseignements", e.target.value)} className="rounded-xl" rows={3} />
      </Field>

      <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-[#EAE6DF]">
        <Button type="button" variant="ghost" onClick={onCancel} className="rounded-full" data-testid="reenroll-cancel">Annuler</Button>
        <Button type="submit" disabled={submitting} className="rounded-full bg-[#C86444] hover:bg-[#A8533A] text-white" data-testid="reenroll-submit">
          {submitting ? "Envoi…" : "Confirmer la réinscription"}
        </Button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-5">
      <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">{title}</h3>
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}

function Grid2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-[#5c6374]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function CheckRow({ label, checked, onChange, detail, onDetail }) {
  return (
    <div>
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
        <span className="text-sm text-[#1A2547]">{label}</span>
      </label>
      {checked && (
        <Input value={detail} onChange={(e) => onDetail(e.target.value)} placeholder="Détail…" className="rounded-xl mt-2" />
      )}
    </div>
  );
}
